import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { AuthModal } from '../components/AuthModal';
import { LoginSignupModal } from '../components/LoginSignupModal';
import { ForgotPasswordModal } from '../components/ForgotPasswordModal';
import { BetaBadge, FeedbackPrompt } from '../components/BetaFeedback';

/**
 * MainLayout - Primary layout component that provides the application's structure
 * 
 * This component:
 * - Creates a consistent page layout with header, main content area, and footer
 * - Handles user authentication state display and logout functionality
 * - Provides conditional navigation based on user role
 * - Uses React Router's Outlet to render child routes within the layout
 * - Provides responsive design with bottom navigation on mobile screens
 */
export const MainLayout: React.FC = () => {
  const userContext = useUser();
  const { showAuthModal, hideAuthModal, authModalState, signupModalTrigger } = userContext;
  const navigate = useNavigate();
  const location = useLocation();

  // State for login/signup modal
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false); // To toggle between login/signup view
  const [showForgotPassword, setShowForgotPassword] = useState(false); // State for forgot password modal

  // Watch for signup modal trigger from UserContext
  useEffect(() => {
    if (signupModalTrigger > 0) {
      setIsSignUp(true);
      setShowLoginModal(true);
    }
  }, [signupModalTrigger]);

  // Access properties based on what's actually defined in UserContextType
  const user = userContext.currentUser;
  const { logout, login, register } = userContext;

  /**
   * Handles user logout by calling the logout function from context
   * and redirecting to the home page
   */
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  /**
   * Handles modal close and resets the signup state
   */
  const handleCloseModal = () => {
    setShowLoginModal(false);
    setIsSignUp(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-ivory">
      {/* Header - Contains navigation and user authentication controls */}
      <header className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo and main navigation links */}
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-800">ThinkSphere</span>
                <BetaBadge />
              </Link>
              {/* Navigation links - hidden on small screens (moved to bottom nav) */}
              <nav className="hidden md:ml-10 md:flex items-center space-x-8">
                {user && (
                  <Link to="/" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                    Dashboard
                  </Link>
                )}
                <Link to="/categories" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                  Browse Quizzes
                </Link>
                {user?.role === 'student' && (
                  <Link to="/history" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                    Quiz History
                  </Link>
                )}
                {/* Conditional navigation for teacher role */}
                {user?.role === 'teacher' && (
                  <Link to="/teacher" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                    Teacher Dashboard
                  </Link>
                )}
                {/* Conditional navigation for admin role */}
                {user?.role === 'admin' && (
                  <Link to="/admin" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                    Admin Dashboard
                  </Link>
                )}
                {/* Account link for all authenticated users */}
                {user && (
                  <Link to="/account" className="text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
                    Account
                  </Link>
                )}
              </nav>
            </div>
            {/* User authentication section - shows different UI based on login status */}
            <div className="flex items-center">
              {user ? (
                <div className="flex items-center space-x-4">
                  <span className="hidden sm:inline text-sm text-neutral-600">
                    Welcome, {user.firstName} {user.lastName}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-neutral-100 text-neutral-700 hover:bg-neutral-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-x-4">
                  <button
                    onClick={() => {
                      setIsSignUp(false);
                      setShowLoginModal(true);
                    }}
                    className="bg-neutral-100 text-neutral-700 hover:bg-neutral-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      setIsSignUp(true);
                      setShowLoginModal(true);
                    }}
                    className="bg-primary text-white hover:bg-primary-dark px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content - Renders child routes via React Router's Outlet */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pb-20 md:pb-6">
          <Outlet />
        </div>
      </main>

      {/* Footer - Beta feedback prompt and copyright information */}
      <footer className="bg-ivory">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-2">
          <FeedbackPrompt showFlagHint={!!user} />
          <p className="text-center text-cool-gray text-sm">
            &copy; {new Date().getFullYear()} ThinkSphere App. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Bottom navigation for mobile screens */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-ivory shadow-warm-lg z-40">
        <div className="flex justify-around items-center h-16">
          {user && (
            <Link
              to="/"
              className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname === '/' ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="text-xs">Dashboard</span>
            </Link>
          )}
          <Link
            to="/categories"
            className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname.startsWith('/categories') ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="text-xs">Categories</span>
          </Link>
          {user?.role === 'student' && (
            <Link
              to="/history"
              className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname.startsWith('/history') ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs">History</span>
            </Link>
          )}
          {user?.role === 'teacher' && (
            <Link
              to="/teacher"
              className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname.startsWith('/teacher') ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="text-xs">Teacher</span>
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname.startsWith('/admin') ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs">Admin</span>
            </Link>
          )}
          {user && (
            <Link
              to="/account"
              className={`flex flex-col items-center p-2 transition-all duration-200 ${location.pathname.startsWith('/account') ? 'text-golden-yellow' : 'text-cool-gray hover:text-midnight-blue'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs">Account</span>
            </Link>
          )}
        </div>
      </div>

      {/* Login/Signup Modal */}
      <LoginSignupModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        isSignUp={isSignUp}
        onLogin={login}
        onRegister={register}
        onForgotPasswordClick={() => setShowForgotPassword(true)}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      {/* Reusable Authentication Modal */}
      <AuthModal
        isOpen={authModalState.isOpen}
        onClose={hideAuthModal}
        onSignUpClick={() => {
          setIsSignUp(true);
          setShowLoginModal(true);
        }}
        onLoginClick={() => {
          setIsSignUp(false);
          setShowLoginModal(true);
        }}
        message={authModalState.message}
        title={authModalState.title}
      />
    </div>
  );
};

export default MainLayout;